import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import {
  Inventory,
  Add,
  History,
  QrCodeScanner,
  Print,
  Settings,
  Dashboard,
  CheckCircle,
  Cancel,
  Upload,
  FileDownload,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { openDialog, addNotification } from '../../store/slices/uiSlice';
import { setFilters, clearFilters } from '../../store/slices/inventorySlice';
import { generatePrintReport } from '../../utils/printReport';
import { exportItemsToCSV } from '../../utils/csvUtils';
import { usePermissions } from '../../hooks/usePermissions';

const Sidebar: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sidebarOpen } = useSelector((state: RootState) => state.ui);
  const { items } = useSelector((state: RootState) => state.inventory);
  const { organization } = useSelector((state: RootState) => state.auth);
  const { canEdit } = usePermissions();

  const handlePrintReport = async () => {
    try {
      dispatch(addNotification({
        message: 'Generating QR codes for print report...',
        type: 'info',
      }));

      await generatePrintReport(items, organization);

      dispatch(addNotification({
        message: 'Report with QR codes generated and print dialog opened',
        type: 'success',
      }));
    } catch (error) {
      console.error('Error generating print report:', error);
      dispatch(addNotification({
        message: 'Failed to generate report with QR codes',
        type: 'error',
      }));
    }
  };
  
  // Calculate statistics
  const totalItems = items.length;
  const checkedOutItems = items.filter(item => item.checkedOut).length;
  const availableItems = totalItems - checkedOutItems;

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <Dashboard />,
      onClick: () => navigate('/dashboard'),
    },
    {
      text: 'All Items',
      icon: <Inventory />,
      onClick: () => {
        dispatch(clearFilters());
        navigate('/inventory');
      },
      badge: totalItems,
    },
    {
      text: 'Available',
      icon: <CheckCircle />,
      onClick: () => {
        dispatch(clearFilters());
        dispatch(setFilters({ checkedInOnly: true }));
        navigate('/inventory');
      },
      badge: availableItems,
      color: 'success' as const,
    },
    {
      text: 'Checked Out',
      icon: <Cancel />,
      onClick: () => {
        dispatch(clearFilters());
        dispatch(setFilters({ checkedOutOnly: true }));
        navigate('/inventory');
      },
      badge: checkedOutItems,
      color: 'error' as const,
    },
  ];

  const actionItems = [
    ...(canEdit ? [{
      text: 'Add Item',
      icon: <Add />,
      onClick: () => dispatch(openDialog({ type: 'add-item' })),
    }] : []),
    {
      text: 'Scan QR Code',
      icon: <QrCodeScanner />,
      onClick: () => dispatch(openDialog({ type: 'qr-scanner' })),
    },
    {
      text: 'History',
      icon: <History />,
      onClick: () => navigate('/history'),
    },
    ...(canEdit ? [
      {
        text: 'Import CSV',
        icon: <Upload />,
        onClick: () => dispatch(openDialog({ type: 'import' })),
      },
      {
        text: 'Export CSV',
        icon: <FileDownload />,
        onClick: () => {
          exportItemsToCSV(items);
          dispatch(addNotification({ message: 'Inventory exported to CSV', type: 'success' }));
        },
      },
    ] : []),
    {
      text: 'Print Report',
      icon: <Print />,
      onClick: handlePrintReport,
    },
  ];

  const drawerWidth = sidebarOpen ? 200 : 60;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          marginTop: '64px', // Height of AppBar
          height: 'calc(100vh - 64px)',
          transition: theme => theme.transitions.create('width', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.standard,
          }),
          overflowX: 'hidden',
        },
      }}
    >
      <Box sx={{ p: sidebarOpen ? 2 : 1 }}>
        {sidebarOpen && (
          <Typography variant="h6" gutterBottom>
            Inventory
          </Typography>
        )}
      </Box>

      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={item.onClick}
              sx={{
                minHeight: 48,
                justifyContent: sidebarOpen ? 'initial' : 'center',
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: sidebarOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {sidebarOpen && (
                <>
                  <ListItemText primary={item.text} />
                  {item.badge !== undefined && (
                    <Chip
                      label={item.badge}
                      size="small"
                      color={item.color || 'default'}
                      variant="outlined"
                    />
                  )}
                </>
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider />

      <List>
        {sidebarOpen && (
          <ListItem>
            <Typography variant="subtitle2" color="text.secondary">
              Actions
            </Typography>
          </ListItem>
        )}
        {actionItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={item.onClick}
              sx={{
                minHeight: 48,
                justifyContent: sidebarOpen ? 'initial' : 'center',
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: sidebarOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {sidebarOpen && <ListItemText primary={item.text} />}
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      {/* Settings at bottom */}
      <List>
        <Divider />
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => dispatch(openDialog({ type: 'settings' }))}
            sx={{
              minHeight: 48,
              justifyContent: sidebarOpen ? 'initial' : 'center',
              px: 2.5,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: sidebarOpen ? 3 : 'auto',
                justifyContent: 'center',
              }}
            >
              <Settings />
            </ListItemIcon>
            {sidebarOpen && <ListItemText primary="Settings" />}
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
};

export default Sidebar;